import React, { useState, useRef, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const OTPInput = ({ 
  length = 6, 
  value = '', 
  onChange, 
  onComplete, 
  disabled = false,
  error = '',
  autoFocus = true
}) => {
  const [otp, setOtp] = useState(value.split('').slice(0, length).concat(Array(length - value.length).fill('')));
  const [showOtp, setShowOtp] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    setOtp(value.split('').slice(0, length).concat(Array(length - value.length).fill('')));
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, val) => {
    // Only allow digits
    if (val && !/^\d$/.test(val)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);

    const otpString = newOtp.join('');
    onChange?.(otpString);

    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete when all digits are filled
    if (otpString.length === length) {
      onComplete?.(otpString);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Focus previous input on left arrow
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      // Focus next input on right arrow
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Home') {
      // Focus first input
      inputRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      // Focus last non-empty input
      for (let i = length - 1; i >= 0; i--) {
        if (otp[i]) {
          inputRefs.current[i]?.focus();
          break;
        }
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < length; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      
      const otpString = newOtp.join('');
      onChange?.(otpString);
      
      if (otpString.length === length) {
        onComplete?.(otpString);
      }
      
      // Focus the next empty input or the last one
      const nextEmptyIndex = newOtp.findIndex(val => !val);
      const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleClick = (index) => {
    // Focus the clicked input, but if it has a value and next input is empty, focus next one
    if (otp[index] && index < length - 1 && !otp[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    } else {
      inputRefs.current[index]?.focus();
    }
  };

  const handleResend = () => {
    // Clear all inputs and focus first one
    const newOtp = Array(length).fill('');
    setOtp(newOtp);
    onChange?.('');
    inputRefs.current[0]?.focus();
  };

  return (
    <div className="w-full">
      {/* OTP Input Fields */}
      <div className="flex justify-center space-x-2 sm:space-x-3 mb-6">
        {Array.from({ length }, (_, index) => (
          <div key={index} className="relative">
            <input
              ref={el => inputRefs.current[index] = el}
              type={showOtp ? 'text' : 'password'}
              value={otp[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined} // Only handle paste on first input
              onClick={() => handleClick(index)}
              disabled={disabled}
              maxLength={1}
              className={`
                w-12 h-14 sm:w-14 sm:h-16 text-center text-xl sm:text-2xl font-bold
                border-2 rounded-lg transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500
                disabled:opacity-50 disabled:cursor-not-allowed
                ${error 
                  ? 'border-red-300 text-red-600 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 text-gray-900 hover:border-gray-400'
                }
                ${otp[index] ? 'bg-whatsapp-50 border-whatsapp-300' : 'bg-white'}
              `}
              placeholder="0"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
            />
            {/* Animated underline for filled inputs */}
            {otp[index] && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-whatsapp-500 to-green-500 rounded-full animate-pulse" />
            )}
          </div>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 text-center">{error}</p>
        </div>
      )}

      {/* Show/Hide OTP Toggle */}
      <div className="flex justify-center mb-4">
        <button
          type="button"
          onClick={() => setShowOtp(!showOtp)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          disabled={disabled}
        >
          {showOtp ? (
            <>
              <EyeSlashIcon className="w-4 h-4 mr-1" />
              Hide OTP
            </>
          ) : (
            <>
              <EyeIcon className="w-4 h-4 mr-1" />
              Show OTP
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-2">
          Enter the 6-digit code sent to your email
        </p>
        <p className="text-xs text-gray-500">
          The code will expire in 10 minutes
        </p>
      </div>
    </div>
  );
};

export default OTPInput;